import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { routeToPath } from "./parseSummary";
import DocsMarkdown from "./DocsMarkdown";

// Small helper: fetch a doc, falling back to a bare .md path if the README
// lookup fails. Returns { markdown, filePath } on success or throws.
//
// Note: CRA's dev server (and SPA hosts in general) reply to unknown paths
// with the app's index.html + HTTP 200 so the client router can take over.
// We treat any response whose Content-Type isn't markdown/plain-text, or
// whose body looks like the app shell, as a miss and move on.
async function tryFetch(path) {
  const base = process.env.PUBLIC_URL || "";
  const res = await fetch(`${base}/docs/${path}`);
  if (!res.ok) return null;
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("text/html")) return null;
  const text = await res.text();
  if (/^\s*<!doctype\s+html/i.test(text)) return null;
  return text;
}

async function fetchDoc(urlPath) {
  const primary = routeToPath(urlPath);
  const primaryText = await tryFetch(primary);
  if (primaryText !== null) {
    return { markdown: primaryText, filePath: primary };
  }

  // Fallback: route might point at a leaf file rather than a folder.
  // e.g. /docs/resources/faq → resources/faq.md
  const rel = urlPath.replace(/^\/docs\/?/, "").replace(/\/$/, "");
  if (rel && !/\.md$/.test(rel)) {
    const fallback = rel + ".md";
    const altText = await tryFetch(fallback);
    if (altText !== null) {
      return { markdown: altText, filePath: fallback };
    }
  }

  throw new Error(`Not found: ${urlPath}`);
}

export default function DocsContent() {
  const location = useLocation();
  const [state, setState] = useState({ status: "loading" });

  // Fetch on route change.
  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });

    fetchDoc(location.pathname)
      .then(({ markdown, filePath }) => {
        if (cancelled) return;
        setState({ status: "ready", markdown, filePath });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: "notfound" });
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Scroll to top on route change (or anchor if present).
  useEffect(() => {
    if (state.status !== "ready") return;
    if (location.hash) {
      // Wait a tick so the new markdown is in the DOM.
      const id = location.hash.replace(/^#/, "");
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [state.status, location.hash, location.pathname]);

  if (state.status === "loading") {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  if (state.status === "notfound") {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
          Page not found
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          The documentation page you're looking for doesn't exist yet.
        </Typography>
        <Button
          component={RouterLink}
          to="/docs"
          variant="contained"
          sx={{ fontWeight: 800, color: "common.white !important" }}
        >
          Back to docs home
        </Button>
      </Box>
    );
  }

  return (
    <DocsMarkdown
      markdown={state.markdown}
      currentFilePath={state.filePath}
    />
  );
}
